// ============================================================================
// Component-specific configuration schemas with requirements
// ============================================================================

import { z } from "zod"

import {
  BulletedListContentSchema,
  ContentBlockSchema,
  FormattedTextContentSchema,
  TextContentJSONSchema,
} from "@tfg/types/schemas/communication-studio/components"
import {
  ContentFilteringPropsSchema,
  ProductAttributesSchema,
} from "@tfg/types/schemas/communication-studio/configurationParameters"
import { FormFileSchema } from "@tfg/types/schemas/file"
import { ProductSheetLanguageSchema } from "@tfg/types/schemas/languages"

import { i18n } from "~/i18n"

export const EcoScoreConfigurationRequiredSchema = z.object({
  ecoScoreDataSource: z
    .string()
    .min(
      1,
      i18n.t("communicationStudio:contentFiltering.errors.ecoScoreDataSource")
    ),
})

export const LogoConfigurationRequiredSchema = z.object({
  logoFile: FormFileSchema,
})

export const ProductAttributesConfigurationRequiredSchema =
  ContentFilteringPropsSchema.pick({
    primaryProductAttributes: true,
    secondaryProductAttributes: true,
  }).extend({
    primaryProductAttributes: z
      .array(ProductAttributesSchema)
      .min(
        1,
        i18n.t(
          "communicationStudio:contentFiltering.errors.primaryProductAttributes"
        )
      ),
  })

export const BulletedListContentRequiredSchema = BulletedListContentSchema.omit(
  { items: true }
).extend({
  items: z.array(
    FormattedTextContentSchema.omit({ text: true }).extend({
      text: z
        .string()
        .min(
          1,
          i18n.t("communicationStudio:contentFiltering.errors.conentBlock")
        ),
    })
  ),
})

export const ContentBlockRequiredSchema = ContentBlockSchema.extend({
  textContent: z
    .array(
      TextContentJSONSchema.omit({ text: true }).extend({
        text: z
          .string()
          .min(
            1,
            i18n.t("communicationStudio:contentFiltering.errors.conentBlock")
          ),
      })
    )
    .or(BulletedListContentRequiredSchema)
    .or(z.array(FormattedTextContentSchema).min(1)),
})

export const ComponentContentRequiredSchema = z.partialRecord(
  ProductSheetLanguageSchema,
  z.array(ContentBlockRequiredSchema)
)

export const StandardsConfigurationRequiredSchema =
  ContentFilteringPropsSchema.pick({
    standardsToBeShown: true,
  }).extend({
    standardsToBeShown: z
      .array(z.number())
      .min(
        1,
        i18n.t("communicationStudio:contentFiltering.errors.standardsToShow")
      ),
  })

export const EcoScoreDisplayModeConfigurationRequiredSchema =
  ContentFilteringPropsSchema.pick({
    ecoScoreDisplayMode: true,
  }).extend({
    ecoScoreDisplayMode: z
      .enum(["b2c", "b2b"])
      .nullable()
      .refine((val) => val !== null, {
        message: i18n.t(
          "communicationStudio:contentFiltering.errors.ecoScoreDisplayMode"
        ),
      }),
  })
